import { useState } from "react";
import { useStore } from "react-redux";

import range from "lodash/range";

// reducers
import { success, error as toastError } from "hyc-store/reducers/toast";

// components
import { Button } from "shared/components/bulma/button";
import { Select } from "shared/components/bulma/select";
import { ActionBar } from "shared/components/action-bar";
import { TagsInput } from "shared/components/bulma/tags-input";


// constants
import * as constants from '../constants';

import * as helper from './helper';
import { historize } from "./historize";
import { useActiveUser } from "shared/hooks/use-active-user";

const MultiSelectFields = [
    { id: 'feel-in-clothes', options: constants.FEEL_IN_CLOTHES, label: 'Feel in clothes', name: 'feel_in_clothes' },
    { id: 'look-at-clothes', options: constants.LOOK_AT_CLOTHES, label: 'Look at clothes', name: 'look_at_clothes' },
    { id: 'cut-of-tops', options: constants.CUT_OF_TOPS, label: 'Cut of tops', name: 'cut_of_tops' },
    { id: 'cut-of-dresses', options: constants.CUT_OF_DRESSES, label: 'Cut of dresses', name: 'cut_of_dresses' },
    { id: 'favourite-pullover', options: constants.FAVOURITE_PULLOVER, label: 'Favorite pullover', name: 'favourite_pullover' },
    { id: 'print-nogo', options: constants.PRINT_NOGO, label: 'Print no-go', name: 'print_nogo' },
    { id: 'colors-nogo', options: constants.COLORS_NOGO, label: 'Colors no-go', name: 'colors_nogo' },
    { id: 'fit-body-shape', options: constants.FIT_BODY_SHAPE, label: 'Fit body shape', name: 'fit_body_shape' },
    { id: 'style-preference', options: constants.STYLE_PREFERENCE, label: 'Brands', name: 'style_preference' }
];

/**
 * transform the value to a string
 * @param {*} param0
 * @returns
 */
const TagsInputWrapper = ({ onChange, ...props }) => {

    const handleChange = (event) => {
        const { name, value } = event.target;

        if (onChange) {
            onChange({
                target: { name, value: value.toString() }
            });
        }
    }

    return (
        <TagsInput
            {...props}
            onChange={handleChange}
        />
    );
}


const Preferences = ({ customer, onUpdate }) => {

    const store = useStore();
    const user = useActiveUser();

    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(false);

    const [data, setData] = useState(customer);
    const [cutOfTops, setCutOfTops] = useState(customer.cut_of_tops);
    const [printNogo, setPrintNogo] = useState(customer.print_nogo);
    const [colorsNogo, setColorsNogo] = useState(customer.colors_nogo);
    const [fitBodyShape, setFitBodyShape] = useState(customer.fit_body_shape);
    const [cutOfDresses, setCutOfDresses] = useState(customer.cut_of_dresses);
    const [feelInClothes, setFeelInClothes] = useState(customer.feel_in_clothes);
    const [lookAtClothes, setLookAtClothes] = useState(customer.look_at_clothes);
    const [stylePreference, setStylePreference] = useState(customer.style_preference);
    const [favouritePullover, setFavouritePullover] = useState(customer.favourite_pullover);

    const handleChange = (event) => {
        const { name, value } = event.target;

        switch (name) {
            case 'cut_of_tops':
                setCutOfTops(value);
                break;
            case 'print_nogo':
                setPrintNogo(value);
                break;
            case 'colors_nogo':
                setColorsNogo(value);
                break;
            case 'fit_body_shape':
                setFitBodyShape(value);
                break;
            case 'cut_of_dresses':
                setCutOfDresses(value);
                break;
            case 'feel_in_clothes':
                setFeelInClothes(value);
                break;
            case 'look_at_clothes':
                setLookAtClothes(value);
                break;
            case 'style_preference':
                setStylePreference(value);
                break;
            case 'favourite_pullover':
                setFavouritePullover(value);
                break;
            default:
                setData({ ...data, [name]: value });
                break;
        }

        setDirty(true);
    }

    const getFieldValue = (fieldName) => {
        switch (fieldName) {
            case 'cut_of_tops':
                return cutOfTops;
            case 'print_nogo':
                return printNogo;
            case 'colors_nogo':
                return colorsNogo;
            case 'fit_body_shape':
                return fitBodyShape;
            case 'cut_of_dresses':
                return cutOfDresses;
            case 'feel_in_clothes':
                return feelInClothes;
            case 'look_at_clothes':
                return lookAtClothes;
            case 'style_preference':
                return stylePreference;
            case 'favourite_pullover':
                return favouritePullover;
            default:
                return;
        }
    }

    const save = async () => {
        const changes = {
            ...data,
            cut_of_tops: cutOfTops ?? '',
            print_nogo: printNogo ?? '',
            colors_nogo: colorsNogo ?? '',
            fit_body_shape: fitBodyShape ?? [],
            cut_of_dresses: cutOfDresses ?? '',
            feel_in_clothes: feelInClothes ?? '',
            look_at_clothes: lookAtClothes ?? '',
            style_preference: stylePreference ?? '',
            favourite_pullover: favouritePullover ?? ''
        };

        setLoading(true);

        try {
            await helper.save(changes);
            const history = historize({ customer, changes });
            await helper.saveHistory(customer, history, user);
            store.dispatch(success('Successfully saved preferences.'));

            setDirty(false);
            onUpdate(+new Date());
        } catch (error) {
            store.dispatch(
                toastError([
                    'Something went wrong while saving the customer preferences, ',
                    'please contact the tech team to report.'
                ].join(' '))
            );
        }

        setLoading(false);
    }

    return (
        <>
            <div className="columns is-multiline">
                <div className="column is-2">
                    <label className="label">Age</label>
                    <Select
                        name="age"
                        value={data.age}
                        options={constants.AGES}
                        onChange={handleChange} />
                </div>
                <div className="column is-4">
                    <label className="label">Type of clothes</label>
                    <Select
                        name="type_of_clothes"
                        value={data.type_of_clothes}
                        options={constants.TYPE_OF_CLOTHES}
                        onChange={handleChange} />
                </div>

                <div className="column is-2">
                    <label className="label">Shirt size</label>
                    <Select
                        name="shirt_size"
                        value={data.shirt_size}
                        options={constants.SHIRT_SIZES}
                        onChange={handleChange} />
                </div>

                <div className="column is-2">
                    <label className="label">Denim size</label>
                    <Select
                        name="denim_size"
                        value={data.denim_size}
                        options={constants.DENIM_SIZES}
                        onChange={handleChange} />
                </div>

                <div className="column is-2">
                    <label className="label">Height</label>
                    <Select
                        name="height"
                        value={data.height}
                        options={range(150, 191).map((r) => `${r} cm`)}
                        onChange={handleChange} />
                </div>


                {MultiSelectFields.map((field) => (
                    <div className="column is-6" key={field.id}>
                        <TagsInputWrapper
                            id={field.id}
                            data={field.options}
                            name={field.name}
                            label={field.label}
                            onChange={handleChange}
                            value={getFieldValue(field.name)}
                            placeholder="Type to add items"
                        />
                    </div>
                ))}

            </div>

            {dirty && (
                <ActionBar>
                    <Button className={`is-primary ${loading ? 'is-loading' : ''}`}
                        onClick={save} disabled={loading}>Save Changes</Button>
                </ActionBar>
            )}
        </>
    );
}

export { Preferences };
